import { GenericDropdown, MENU_TYPE } from '../../../common/components/generic-dropdown'
import { Button } from '../../../common/components/button'

import { textTableService } from '../../blocks/table/text-table.service'
import { useStyles } from './table-toolbar.style'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faTableColumns } from '@fortawesome/pro-light-svg-icons'

export const TableToolbar = function ({ block, table, setTable, currentDataCell, currentHeaderCell, onDelete }) {
  const classes = useStyles()

  function insertRowBelow () {
    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    insertRowAt(row + 1)
  }

  function insertRowAbove () {
    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    insertRowAt(row)
  }

  function insertRowAt (index) {
    const newRow = textTableService.generateEmptyRowWithKeys(table)
    block.props.data.splice(index, 0, textTableService.generateEmptyRow(table, false))
    table.data.splice(index, 0, newRow)
    setTable({
      header: table.header,
      data: table.data,
      html: table.html
    })
  }

  function insertColumnLeft () {
    const columnIndex = getCurrentColumnIndex()

    if (columnIndex !== undefined) {
      insertColumnAt(columnIndex)
    }
  }

  function insertColumnRight () {
    const columnIndex = getCurrentColumnIndex()

    if (columnIndex !== undefined) {
      insertColumnAt(columnIndex + 1)
    }
  }

  function getCurrentColumnIndex () {
    let columnIndex

    if (currentDataCell !== undefined) {
      const location = textTableService.getDataCellLocation(table, currentDataCell.key)
      columnIndex = location.column
    } else if (currentHeaderCell !== undefined) {
      columnIndex = textTableService.getHeaderCellLocation(table, currentHeaderCell.key)
    }

    return columnIndex
  }

  function insertColumnAt (index) {
    block.props.header.splice(index, 0, textTableService.generateEmptyCell(false))
    block.props.data.forEach((row) => {
      row.splice(index, 0, textTableService.generateEmptyCell(false))
    })
    table.header.splice(index, 0, textTableService.generateEmptyCell(true))
    table.data.forEach(({ row }) => {
      row.splice(index, 0, textTableService.generateEmptyCell(true))
    })
    setTable({
      header: table.header,
      data: table.data,
      html: table.html
    })
  }

  function deleteCurrentRow () {
    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    block.props.data.splice(row, 1)
    table.data.splice(row, 1)
    setTable({
      header: table.header,
      data: table.data,
      html: table.html
    })
    onDelete()
  }

  function deleteCurrentColumn () {
    const columnIndex = getCurrentColumnIndex()

    block.props.header.splice(columnIndex, 1)
    block.props.data.forEach((row) => {
      row.splice(columnIndex, 1)
    })
    table.header.splice(columnIndex, 1)
    table.data.forEach(({ row }) => {
      row.splice(columnIndex, 1)
    })
    setTable({
      header: table.header,
      data: table.data,
      html: table.html
    })
    onDelete()
  }

  function moveColumnLeft () {
    const columnIndex = getCurrentColumnIndex()

    if (columnIndex !== undefined) {
      moveColumn(columnIndex, columnIndex - 1)
    }
  }

  function moveColumnRight () {
    const columnIndex = getCurrentColumnIndex()

    if (columnIndex !== undefined) {
      moveColumn(columnIndex, columnIndex + 1)
    }
  }

  function moveColumn (from, to) {
    let cellToMove = block.props.header[from]
    block.props.header.splice(from, 1)
    block.props.header.splice(to, 0, cellToMove)
    block.props.data.forEach((row) => {
      cellToMove = row[from]
      row.splice(from, 1)
      row.splice(to, 0, cellToMove)
    })

    cellToMove = table.header[from]
    table.header.splice(from, 1)
    table.header.splice(to, 0, cellToMove)
    table.data.forEach(({ row }) => {
      cellToMove = row[from]
      row.splice(from, 1)
      row.splice(to, 0, cellToMove)
    })

    setTable({ ...table })
  }

  function moveRowUp () {
    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    moveRow(row, row - 1)
  }

  function moveRowDown () {
    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    moveRow(row, row + 1)
  }

  function moveRow (from, to) {
    let rowToMove = block.props.data[from]
    block.props.data.splice(from, 1)
    block.props.data.splice(to, 0, rowToMove)

    rowToMove = table.data[from]
    table.data.splice(from, 1)
    table.data.splice(to, 0, rowToMove)

    setTable({ ...table })
  }

  function resetColumnWidths () {
    block.props.header.forEach((cell) => {
      cell.width = ''
    })

    table.header.forEach((cell) => {
      cell.width = ''
    })

    setTable({ ...table })
  }

  function areRowInsertsDisabled () {
    return (currentDataCell === undefined) || (currentHeaderCell !== undefined)
  }

  function areRowDeletionsDisabled () {
    return areRowInsertsDisabled() || (block.props.data.length < 2)
  }

  function areColumnInsertsDisabled () {
    return (currentDataCell === undefined) && (currentHeaderCell === undefined)
  }

  function areColumnDeletionsDisabled () {
    return areColumnInsertsDisabled() || (block.props.header.length < 2)
  }

  function isColumnMoveLeftDisabled () {
    const columnIndex = getCurrentColumnIndex()
    return areColumnInsertsDisabled() || (columnIndex === 0)
  }

  function isColumnMoveRightDisabled () {
    const columnIndex = getCurrentColumnIndex()
    return areColumnInsertsDisabled() || (columnIndex >= (table.header.length - 1))
  }

  function isRowMoveUpDisabled () {
    if (areRowInsertsDisabled()) {
      return true
    }

    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    return row === 0
  }

  function isRowMoveDownDisabled () {
    if (areRowInsertsDisabled()) {
      return true
    }

    const { row } = textTableService.getDataCellLocation(table, currentDataCell.key)
    return row >= (table.data.length - 1)
  }

  return (
    <div className={classes.toolbar}>
      <GenericDropdown
        Target={
          <div className={classes.rowButton}>
            <FontAwesomeIcon className={classes.rowIcon} icon={faTableColumns}/>
            <FontAwesomeIcon className={classes.downCaret} icon={faCaretDown}/>
          </div>
        }
        DropdownItems={[
          <Button key="insert-row-above" onClick={insertRowAbove} className={areRowInsertsDisabled() ? classes.disable : ''}>Insert Row Above</Button>,
          <Button key="insert-row-below" onClick={insertRowBelow} className={areRowInsertsDisabled() ? classes.disable : ''}>Insert Row Below</Button>,
          <Button key="move-row-up" onClick={moveRowUp} className={isRowMoveUpDisabled() ? classes.disable : ''}>Move Row Up</Button>,
          <Button key="move-row-down" onClick={moveRowDown} className={isRowMoveDownDisabled() ? classes.disable : ''}>Move Row Down</Button>,
          <Button key="delete-row" onClick={deleteCurrentRow} className={areRowDeletionsDisabled() ? classes.disable : ''}>Delete Row</Button>
        ]}
        menuType={MENU_TYPE.SMALL}
      />
      <GenericDropdown
        Target={
          <div className={classes.columnButton}>
            <FontAwesomeIcon className={classes.columnIcon} icon={faTableColumns}/>
            <FontAwesomeIcon className={classes.downCaret} icon={faCaretDown}/>
          </div>
        }
        DropdownItems={[
          <Button key="insert-column-left" onClick={insertColumnLeft} className={areColumnInsertsDisabled() ? classes.disable : ''}>Insert Column Left</Button>,
          <Button key="insert-column-berightlow" onClick={insertColumnRight} className={areColumnInsertsDisabled() ? classes.disable : ''}>Insert Column Right</Button>,
          <Button key="move-column-left" onClick={moveColumnLeft} className={isColumnMoveLeftDisabled() ? classes.disable : ''}>Move Column Left</Button>,
          <Button key="move-column-right" onClick={moveColumnRight} className={isColumnMoveRightDisabled() ? classes.disable : ''}>Move Column Right</Button>,
          <Button key="delete-column" onClick={deleteCurrentColumn} className={areColumnDeletionsDisabled() ? classes.disable : ''}>Delete Column</Button>,
          <Button key="reset-column-widths" onClick={resetColumnWidths}>Reset Column Widths</Button>
        ]}
        menuType={MENU_TYPE.SMALL}
      />
    </div>
  )
}
